/* eslint-disable @typescript-eslint/unbound-method */
import { ConfiguratorId } from "#models/Configurator"
import * as Effect from "effect-app/Effect"
import { UserProfileId } from "effect-app/ids"
import { Email, NonEmptyString255, NonEmptyString2k } from "effect-app/Schema"
import type { Brand } from "effect/Brand"
import * as S from "./schema.ts"
import { type HasTimestamps, TimestampProps } from "./Timestamps.ts"

export const FirstName = NonEmptyString255
  .pipe(S.withDefaultMake)

export type FirstName = typeof FirstName.Type

export const LastName = NonEmptyString255
  .pipe(S.withDefaultMake)

export type LastName = typeof LastName.Type

export class FullName extends S.Opaque<FullName, FullName.Encoded>()(
  S
    .Struct({
      firstName: FirstName,
      lastName: LastName
    })
    .annotate({ identifier: "FullName" })
) {
  static render(this: void, fn: FullName) {
    return NonEmptyString2k(`${fn.firstName} ${fn.lastName}`)
  }

  static create(this: void, firstName: FirstName, lastName: LastName) {
    return FullName.make({ firstName, lastName })
  }
}

export const UserId = UserProfileId
export type UserId = UserProfileId

export const Role = S.Literals(["manager", "user"]).pipe(S.withDefaultMake)
export type Role = typeof Role.Type

export const Permission = S.Literals([
  "backoffice.access",
  "dxf_optimizer.access",
  "settings.manage"
])
export type Permission = typeof Permission.Type

export const allPermissions: ReadonlyArray<Permission> = Permission.literals

export const permissionCatalog: ReadonlyArray<{ readonly permission: Permission; readonly title: string }> = [
  { permission: "backoffice.access", title: "Backoffice" },
  { permission: "dxf_optimizer.access", title: "DXF optimizer" },
  { permission: "settings.manage", title: "Manage settings" }
]

export const UserPermissions = S.Array(Permission).pipe(
  S.check(S.isUnique()),
  S.withConstructorDefault(Effect.succeed(allPermissions))
)

export const hasPermission = (
  user: { readonly permissions: ReadonlyArray<Permission> },
  permission: Permission
) => user.permissions.includes(permission)

export const canGrantPermission = (
  actor: { readonly permissions: ReadonlyArray<Permission> },
  permission: Permission
) => hasPermission(actor, permission)

export const canGrantPermissions = (
  actor: { readonly permissions: ReadonlyArray<Permission> },
  permissions: ReadonlyArray<Permission>
) => permissions.every((permission) => canGrantPermission(actor, permission))

export const setPermission = (
  permissions: ReadonlyArray<Permission>,
  permission: Permission,
  granted: boolean
): ReadonlyArray<Permission> => {
  if (granted) return permissions.includes(permission) ? permissions : [...permissions, permission]
  return permissions.includes(permission) ? permissions.filter((current) => current !== permission) : permissions
}

const normalizeStoredPermission = (permission: string): Permission | null => {
  if (permission === "permissions.manage" || permission === "settings.manage") return "settings.manage"
  if (permission === "dxf_optimizer.access") return "dxf_optimizer.access"
  if (permission === "backoffice.access") return "backoffice.access"
  return null
}

export const migrateUserPermissions = <A extends object>(
  user: A & { readonly permissions?: ReadonlyArray<string> }
): Omit<A, "permissions"> & { permissions: ReadonlyArray<Permission> } => {
  if (user.permissions === undefined) {
    return { ...user, permissions: allPermissions }
  }
  const raw = user.permissions
  // One-shot legacy upgrade: pre-backoffice catalog stored `permissions.manage`.
  // After a modern save that list is rewritten without that string, so intentional
  // revokes of backoffice.access are not re-applied.
  const legacyPermissionsManage = raw.includes("permissions.manage")
  const permissions: Array<Permission> = []
  for (const entry of raw) {
    const permission = normalizeStoredPermission(entry)
    if (permission !== null && !permissions.includes(permission)) {
      permissions.push(permission)
    }
  }
  if (legacyPermissionsManage && !permissions.includes("backoffice.access") && permissions.length > 0) {
    return { ...user, permissions: ["backoffice.access", ...permissions] }
  }
  return { ...user, permissions }
}

const samePermissions = (
  left: ReadonlyArray<string> | undefined,
  right: ReadonlyArray<string>
) =>
  left !== undefined
  && left.length === right.length
  && left.every((permission, index) => permission === right[index])

export type UserPermissionsRewrite = {
  readonly id: string
  readonly email: string
  readonly from: ReadonlyArray<string> | undefined
  readonly to: ReadonlyArray<Permission>
}

/** Rows the offline Users cutover must persist. Seed markers (no email) are skipped. */
export const planUserPermissionsRewrite = (
  user: { readonly id: string; readonly email?: string; readonly permissions?: ReadonlyArray<string> }
): UserPermissionsRewrite | null => {
  if (typeof user.email !== "string") return null
  const next = migrateUserPermissions(user)
  if (samePermissions(user.permissions, next.permissions)) return null
  return { id: user.id, email: user.email, from: user.permissions, to: next.permissions }
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface PasswordBrand extends Brand<"Password"> {
}

export type Password = string & PasswordBrand

const complexity = /(?=^.{8,50}$)(?=.*\d)(?=.*[!@#$%^&*,.]+)(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/
function verifyPasswordComplexity(_: string): _ is Password {
  return !!_.match(complexity)
}

const symbols = "!@#$%^&*,."
export const passwordString = S.String.pipe(
  S.refine(
    verifyPasswordComplexity,
    {
      message:
        "Passwort muss mindestens 8 Zeichen, einen Großbuchstaben, einen Kleinbuchstaben, eine Zahl und ein Sonderzeichen enthalten, z. B. "
        + symbols
    }
  )
)

export const Password = Object
  .assign(
    S.RedactedFromValue(passwordString, { label: "Password" }).pipe(S.withDefaultMake),
    { symbols }
  )
export type PasswordSecret = typeof Password.Type

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface PasswordHashBrand extends Brand<"PasswordHash"> {}

export type PasswordHash = string & PasswordHashBrand
export const passwordHash = S.String.pipe(
  S.check(S.isMinLength(1), S.isMaxLength(255)),
  // arbitrary removes brand benefit
  S.fromBrand(S.nominal<PasswordHash>())
)

export const PasswordHash = S
  .RedactedFromValue(passwordHash, { label: "PasswordHash" })
  .annotate({ identifier: "PasswordHash" })
  .pipe(S.withDefaultMake)
export type PasswordHashSecret = typeof PasswordHash.Type

export const JsonEncodablePasswordHash = S
  .JsonEncodableRedactedFromValue(passwordHash, { label: "PasswordHash" })
  .annotate({ identifier: "PasswordHash" })
  .pipe(S.withDefaultMake)

export class User extends S.Opaque<User, User.Encoded>()(S.Struct({
  id: UserId.withConstructorDefault,
  name: FullName,
  email: Email,
  password: PasswordHash,
  permissions: UserPermissions,
  // Decoding default: store projections (the Users list) skip jitM, so legacy rows must still decode.
  favoriteConfiguratorIds: S.Array(ConfiguratorId).pipe(
    S.withDecodingDefault(Effect.succeed([])),
    S.withConstructorDefault(Effect.succeed([]))
  ),
  ...TimestampProps
  // role: S.Literal("manager", "user")
})) implements HasTimestamps {
}

export type PermissionChangeDenial = "not-manager" | "self-update" | "final-manager" | "missing-grant"

export const getPermissionChangeDenial = (
  actor: { readonly id: string; readonly permissions: ReadonlyArray<Permission> },
  target: { readonly id: string; readonly permissions: ReadonlyArray<Permission> },
  permission: Permission,
  granted: boolean,
  users: ReadonlyArray<{ readonly permissions: ReadonlyArray<Permission> }>
): PermissionChangeDenial | null => {
  if (!hasPermission(actor, "settings.manage")) return "not-manager"
  if (actor.id === target.id) return "self-update"
  if (granted && !canGrantPermission(actor, permission)) return "missing-grant"
  if (
    permission === "settings.manage"
    && !granted
    && hasPermission(target, permission)
    && users.filter((user) => hasPermission(user, permission)).length <= 1
  ) return "final-manager"
  return null
}

export function showUser(user: User) {
  return user.name
}

// codegen:start {preset: model}
//
export namespace FullName {
  export interface Encoded extends S.StructNestedEncoded<typeof FullName> {}
}
export namespace User {
  export interface Encoded extends S.StructNestedEncoded<typeof User> {}
}
//
// codegen:end
//
